import type { z } from 'zod';

export enum StorageType {
  LOCAL = 'LOCAL',
  SESSION = 'SESSION',
}

const USER_STORAGE_SEPARATOR = ':';

const STORAGE_MAPPING: { [key in StorageType]: globalThis.Storage } = {
  [StorageType.LOCAL]: localStorage,
  [StorageType.SESSION]: sessionStorage,
};

export class Storage<TData> {
  readonly name: string;
  readonly type: StorageType;
  private readonly store: globalThis.Storage;

  constructor(name: string, type = StorageType.LOCAL) {
    this.name = `EDealer.${name}`;
    this.type = type;
    this.store = STORAGE_MAPPING[this.type];
  }

  /**
   * Reads and JSON-parses the stored value. Pass an optional Zod `schema` to
   * validate the parsed shape at this boundary: on a parse or shape failure the
   * entry is self-healed (removed) so the caller falls back to its default
   * instead of receiving stale/corrupt data.
   *
   * NOTE: `Storage` instances are cached per key name, so self-heal removal is a
   * shared-state mutation — a given storage key should have a single schema-owner.
   * If two readers validate the same key with different schemas, the first failure
   * removes the entry for every reader (including ones that would have accepted it).
   *
   * @param schema - Optional Zod schema; when provided, the parsed value is validated against it.
   */
  get(schema?: z.ZodType<TData>): TData | undefined {
    let parsed: unknown;
    try {
      const item = this.store.getItem(this.name);
      if (item === null) {
        return undefined;
      }
      parsed = JSON.parse(item);
    } catch (error) {
      /*
       * Corrupt data (or unavailable storage) — self-heal by clearing, then surface the corruption to
       * Datadog. The raw error is NOT forwarded: V8's JSON.parse SyntaxError embeds a prefix of the
       * invalid input in its message, which for secret-bearing keys (auth token, impersonation state)
       * would leak stored bytes. Log a sanitized error carrying only the error class + storage name.
       * LoggingService is imported lazily to avoid a module cycle
       * (storageUtils -> LoggingService -> loggingUtils -> storageUtils).
       */
      this.removeQuietly();
      if (error instanceof Error) {
        const sanitizedError = new Error(`Corrupt data in storage "${this.name}" (${error.name})`);
        void import('components/core/logging/LoggingService')
          .then(({ default: LoggingService }) => {
            LoggingService.captureException(sanitizedError, { info: `Error reading storage "${this.name}"` });
          })
          .catch(() => {});
      }

      return undefined;
    }

    if (!schema) {
      return parsed as TData;
    }

    const result = schema.safeParse(parsed);
    if (result.success) {
      return result.data;
    }

    this.removeQuietly();

    return undefined;
  }

  getIn(key: keyof TData) {
    const storage = this.get();

    return storage?.[key];
  }

  remove() {
    try {
      this.store.removeItem(this.name);
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new Error(`Error removing storage with name ${this.name}. \n ${error}`, { cause: error });
    }

    return this;
  }

  removeIn(key: keyof TData) {
    const parent = this.get();
    if (!parent) {
      return this;
    }
    delete parent[key];
    this.store.setItem(this.name, JSON.stringify(parent));

    return this;
  }

  set(value: TData) {
    try {
      /*
       * A null/undefined value means "clear this entry": remove it rather than persist it. Persisting would
       * hit JSON.stringify(undefined), which returns the JS value `undefined` (not a string); localStorage
       * then coerces that to the literal string "undefined", and the next get() throws a SyntaxError on
       * JSON.parse("undefined"). The prior `value !== null || value !== undefined` guard was a tautology
       * (always true), so it never skipped the corrupt write.
       */
      if (value === null || value === undefined) {
        /*
         * Best-effort clear: a failed removal is swallowed (mirrors get()'s self-heal contract),
         * since the next get() returns undefined regardless of whether removeItem succeeded.
         */
        this.removeQuietly();

        return this;
      }
      this.store.setItem(this.name, JSON.stringify(value));
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new Error(`Error setting ${value} in storage ${this.name}. \n ${error}`, { cause: error });
    }

    return this;
  }

  setIn(childKey: keyof TData, value: any) {
    const toSave: any = this.get() || {};
    toSave[childKey] = value;
    this.store.setItem(this.name, JSON.stringify(toSave));

    return this;
  }

  private removeQuietly() {
    try {
      this.store.removeItem(this.name);
    } catch {}
  }
}

class StorageManager {
  private managedStorage: { [key: string]: Storage<any> } = {};

  constructor() {
    this.loadStorage();
  }

  clearStorage(options: {
    forUserId?: string;
    ofType: StorageType;
    /**
     * The specific `sectionPath` to target when clearing.
     * Can be the name or full path.
     * e.g. `retail` or `/retail`
     */
    sectionPath?: string;
  }) {
    const { forUserId, ofType, sectionPath } = options;
    const storage = STORAGE_MAPPING[ofType];
    const entries = Object.entries(storage);

    if (forUserId) {
      const nameSuffix = this.getStorageName({ forUserId, withKey: '' });
      for (const [key, _] of entries) {
        let clearCondition = key.endsWith(nameSuffix);
        if (sectionPath) {
          const sectionPathName = sectionPath.replaceAll('/', '');
          clearCondition = key.endsWith(nameSuffix) && key.includes(sectionPathName);
        }
        if (clearCondition) {
          storage.removeItem(key);
        }
      }
    } else {
      for (const [key, _] of entries) {
        storage.removeItem(key);
      }
    }
  }

  createOrFetchStorage<TData>(name: string, type?: StorageType, forUserId?: string): Storage<TData> {
    name = this.getStorageName({ forUserId, withKey: name });
    if (this.managedStorage[name] && type === this.managedStorage[name].type) {
      return this.managedStorage[name];
    }
    const newStorage = new Storage<TData>(name, type);
    this.managedStorage[name] = newStorage;

    return newStorage;
  }

  getStorageName(options: { forUserId?: string; withKey: string }) {
    return options.forUserId ? `${options.withKey}${USER_STORAGE_SEPARATOR}${options.forUserId}` : options.withKey;
  }

  private loadStorage() {
    for (const [type, storage] of Object.entries(STORAGE_MAPPING)) {
      this.loopAndLoad(storage, type as StorageType);
    }
  }

  private loopAndLoad(storage: globalThis.Storage, storageType: StorageType) {
    for (const k of Object.keys(storage)) {
      const removeNamespace = k.replace('EDealer.', '');
      if (!this.managedStorage[removeNamespace]) {
        this.managedStorage[removeNamespace] = new Storage(removeNamespace, storageType);
      }
    }
  }
}

export const storageManager = new StorageManager();
